import {
    QualityHubAction,
    QualityHubActionTypes,
    OfflineQueueOperation,
    SignatureQueueOperation,
} from "../types";
import { uniqueId } from "src/utils";

export type LocallyStoredTaskList
    = Spintr.TaskList
    & { };

/**
 * The state of the Quality Hub.
 */
export type QualityHubState = {
    /**
     * The Uber ID for which the task lists were last fetched. If this has a
     * value, that means we are in overview mode (viewing another node's lists).
     */
    listsFetchedForUberId?: number | undefined;

    /**
     * A map of task lists by their IDs.
     */
    listsById: { [id: number]: Spintr.TaskList };

    /**
     * An array of all known task list IDs.
     */
    allListIds: number[];

    /**
     * An array of task list IDs that are accessible to the user.
     */
    myTaskListsIds: number[];

    /**
     * A map of loader states for task lists by their IDs.
     */
    listsLoading: { [id: number]: boolean };

    /**
     * Information about 'My Lists' loading state.
     */
    myLists: {
        /**
         * Whether the 'My Lists' are loading or not.
         */
        loading: boolean;

        /**
         * Whether the 'My Lists' have been queried or not.
         */
        hasQueried: boolean;
    },

    /**
     * A map of resource tags by their IDs.
     */
    resourceTagsByIds: { [id: number]: Spintr.ResourceTag };

    /**
     * An array of all known resource tag IDs.
     */
    allTagIds: number[];

    /**
     * An array of the IDs of the resource tags that are part of the user's
     * latest query.
     */
    tagsQueryWindow: number[];

    /**
     * Whether the resource tags are loading or not.
     */
    loadingResourceTags: boolean;

    /**
     * Loaded PIN hashes for local signature verification.
     */
    pinHashes: string[];

    /**
     * Whether PIN hashes are being loaded.
     */
    loadingPinHashes: boolean;

    /**
     * Whether PIN hashes have been queried at least once.
     */
    hasLoadedPinHashes: boolean;

    /**
     * A queue for offline operations that need to be processed later.
     */
    offlineQueue: {
        /**
         * 
         */
        operations: OfflineQueueOperation[];

        /**
         * 
         */
        isProcessing: boolean;

        /**
         *
         */
        lastRetryAttempt: Date | undefined;
    },

    /**
     * Queue for signature-required status changes that still need local PIN
     * validation before they are submitted.
     */
    signatureQueue: {
        operations: SignatureQueueOperation[];
        isSubmitting: boolean;
        lastSubmitError?: string | undefined;
    },

    operationalTargets: {
        items: Spintr.OperationalTarget[];

        hasLoaded: boolean;

        isLoading: boolean;
    }
};

/**
 * The initial state of the Quality Hub.
 */
const initialState: QualityHubState = {
    listsById: {},
    allListIds: [],
    myTaskListsIds: [],
    listsLoading: {},
    myLists: {
        loading: false,
        hasQueried: false,
    },
    resourceTagsByIds: {},
    allTagIds: [],
    tagsQueryWindow: [],
    loadingResourceTags: false,
    pinHashes: [],
    loadingPinHashes: false,
    hasLoadedPinHashes: false,
    offlineQueue: {
        operations: [],
        isProcessing: false,
        lastRetryAttempt: undefined,
    },
    signatureQueue: {
        operations: [],
        isSubmitting: false,
        lastSubmitError: undefined,
    },
    operationalTargets: {
        items: [],
        hasLoaded: false,
        isLoading: false,
    },
};


/**
 * The reducer function for the Quality Hub.
 *
 * @param {QualityHubState} state - The current state of the Quality Hub.
 * @param {QualityHubAction} action - The action to be processed.
 * @returns {QualityHubState} The new state of the Quality Hub.
 */
export function qualityHubReducer(
    state: QualityHubState = initialState,
    action: QualityHubAction,
): QualityHubState {
    switch (action.type) {
    case QualityHubActionTypes.TaskListCreated:
    case QualityHubActionTypes.TaskListUpdated:
        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.list.id]: action.list,
            },
            allListIds: state.allListIds.every(id => id !== action.list.id)
                ? [...state.allListIds, action.list.id]
                : state.allListIds,
        };

    case QualityHubActionTypes.TaskListDeleted: {
        const newListsById = { ...state.listsById };
        delete newListsById[action.id];

        return {
            ...state,
            listsById: newListsById,
            allListIds: state.allListIds.filter(id => id !== action.id),
        };
    }

    case QualityHubActionTypes.TaskListGroupCreated:
    case QualityHubActionTypes.TaskListGroupUpdated:
        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.group.listId]: {
                    ...state.listsById[action.group.listId],
                    groups: [
                        ...(state.listsById[action.group.listId]?.groups || [])
                            .filter((group) => group.id !== action.group.id),
                        action.group,
                    ],
                },
            },
        };

    case QualityHubActionTypes.TaskListGroupDeleted:
        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.listId]: {
                    ...state.listsById[action.listId],
                    groups: state.listsById[action.listId]?.groups?.filter(
                        group => group.id !== action.groupId,
                    ),
                },
            },
        };

    case QualityHubActionTypes.TaskListItemCreated:
    case QualityHubActionTypes.TaskListItemUpdated:
        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.item.listId]: {
                    ...state.listsById[action.item.listId],
                    tasks: [
                        ...(state.listsById[action.item.listId]?.tasks || [])
                            .filter(item => item.id !== action.item.id),
                        action.item,
                    ],
                },
            },
        };

    case QualityHubActionTypes.TaskListItemDeleted:
        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.listId]: {
                    ...state.listsById[action.listId],
                    tasks: state.listsById[action.listId]?.tasks?.filter(
                        item => item.id !== action.itemId,
                    ),
                },
            },
        };

    case QualityHubActionTypes.TaskListItemStatusCreated:
    case QualityHubActionTypes.TaskListItemStatusUpdated:
        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.listId]: {
                    ...state.listsById[action.listId],
                    statuses: [
                        ...(state.listsById[action.listId]?.statuses || [])
                            .filter((status) => status.id !== action.status.id),
                        action.status,
                    ],
                },
            },
        };

    case QualityHubActionTypes.TaskListItemStatusDeleted:
        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.listId]: {
                    ...state.listsById[action.listId],
                    statuses: state.listsById[action.listId]?.statuses?.filter(
                        status => status.id !== action.statusId,
                    ),
                },
            },
        };
    
    case QualityHubActionTypes.ResourceTagCreated:
    case QualityHubActionTypes.ResourceTagUpdated:
        return {
            ...state,
            resourceTagsByIds: {
                ...state.resourceTagsByIds,
                [action.tag.id]: action.tag,
            },
            allTagIds: state.allTagIds.every(id => id !== action.tag.id)
                ? [...state.allTagIds, action.tag.id]
                : state.allTagIds,
        };

    case QualityHubActionTypes.ResourceTagDeleted:
        
        return {
            ...state,
            resourceTagsByIds: Object.entries(state.resourceTagsByIds)
                .filter(([entry]) => entry[0] !== String(action.tagId))
                .reduce((acc, [id, tag]) => ({
                    ...acc,
                    [id]: tag,
                }), {}),
            allTagIds: state.allTagIds.filter(id => id !== action.tagId),
        };

    case QualityHubActionTypes.QueryTaskListsFulfilled:
        return {
            ...state,
            listsById: action.payload.items.reduce((acc, item) => {
                acc[item.id] = item;
                return acc;
            }, { ...state.listsById }),
            allListIds: state.allListIds.concat(
                action.payload.items
                    .filter((item) => state.allListIds.every(id => id !== item.id))
                    .map((item) => item.id),
            ),
        };

    case QualityHubActionTypes.QueryMyTaskListsPending:
        if (action.meta.nodeId !== state.listsFetchedForUberId) {
            // If we are querying for a different node, reset the my lists state
            return {
                ...state,
                listsFetchedForUberId: undefined,
                myTaskListsIds: [],
                allListIds: [],
                listsById: {},
                myLists: {
                    loading: true,
                    hasQueried: true,
                },
            };
        }

        return {
            ...state,
            myLists: {
                ...state.myLists,
                loading: true,
                hasQueried: true,
            },
        };

    case QualityHubActionTypes.QueryMyTaskListsRejected:
        return {
            ...state,
            myLists: {
                ...state.myLists,
                loading: false,
            },
        };

    case QualityHubActionTypes.QueryMyTaskListsFulfilled:
        return {
            ...state,
            listsFetchedForUberId: action.meta.nodeId,
            listsById: action.payload.reduce((acc, item) => {
                acc[item.id] = item;
                return acc;
            }, state.listsById),
            allListIds: state.allListIds.concat(
                action.payload
                    .filter((item) => state.allListIds.every(id => id !== item.id))
                    .map((item) => item.id),
            ),
            myTaskListsIds: action.payload.map(item => item.id),
            myLists: {
                ...state.myLists,
                loading: false,
            },
        };

    case QualityHubActionTypes.FindTaskListPending:
        return {
            ...state,
            listsLoading: {
                ...state.listsLoading,
                [action.meta.id]: true,
            },
        };

    case QualityHubActionTypes.FindTaskListRejected:
        // TODO: Possibly include error here?
        return {
            ...state,
            listsLoading: {
                ...state.listsLoading,
                [action.meta.id]: false,
            },
        };

    case QualityHubActionTypes.FindTaskListFulfilled:
        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.payload.id]: action.payload,
            },
            allListIds: state.allListIds.every(id => id !== action.payload.id)
                ? [...state.allListIds, action.payload.id]
                : state.allListIds,
            listsLoading: {
                ...state.listsLoading,
                [action.meta.id]: false,
            },
        };


    case QualityHubActionTypes.CreateTaskStatusPending:
        if (!state.listsById[action.meta.listId]) {
            return state;
        }

        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.meta.listId]: {
                    ...state.listsById[action.meta.listId],
                    // Add the temporary status to the list
                    statuses: [
                        ...(state.listsById[action.meta.listId]?.statuses || []),
                        action.meta.model as Spintr.TaskListItemStatus,
                    ],
                },
            },
        };

    case QualityHubActionTypes.CreateTaskStatusRejected:
        if (!state.listsById[action.meta.listId]) {
            return state;
        }

        if (action.payload.isOffline) {
            return {
                ...state,
                offlineQueue: {
                    ...state.offlineQueue,
                    operations: state.offlineQueue.operations.concat({
                        id: uniqueId("QH-OFFLINE-OP_"),
                        type: "CREATE_STATUS",
                        data: action.meta,
                    }),
                },
                signatureQueue: !action.meta.model.signatureQueueItemId
                    ? state.signatureQueue
                    : {
                        ...state.signatureQueue,
                        operations: state.signatureQueue.operations.filter(
                            (op) => op.id !== action.meta.model.signatureQueueItemId,
                        ),
                    },
            };
        }

        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.meta.listId]: {
                    ...state.listsById[action.meta.listId],
                    // Remove the temporary status
                    statuses: state.listsById[action.meta.listId]?.statuses?.filter(
                        status => status !== action.meta.model,
                    ),
                },
            },
            signatureQueue: !action.meta.model.signatureQueueItemId
                ? state.signatureQueue
                : {
                    ...state.signatureQueue,
                    lastSubmitError: "PIN validation failed on server. Please try again.",
                },
        };

    case QualityHubActionTypes.CreateTaskStatusFulfilled:
        if (!state.listsById[action.meta.listId]) {
            return state;
        }

        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.meta.listId]: {
                    ...state.listsById[action.meta.listId],
                    statuses: [
                        // Replace the temporary status with the real one
                        ...(state.listsById[action.meta.listId]?.statuses || [])
                            .concat({
                                ...action.payload,
                                reportedOn: new Date(action.payload.reportedOn),
                            })
                            .filter(status => status !== action.meta.model),
                    ],
                },
            },
            signatureQueue: !action.meta.model.signatureQueueItemId
                ? state.signatureQueue
                : {
                    ...state.signatureQueue,
                    operations: state.signatureQueue.operations.filter(
                        (op) => op.id !== action.meta.model.signatureQueueItemId,
                    ),
                },
        };

    case QualityHubActionTypes.UpdateTaskStatusPending:
        if (!state.listsById[action.meta.listId]) {
            return state;
        }

        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.meta.listId]: {
                    ...state.listsById[action.meta.listId],
                    statuses: [
                        // Update the status in advance
                        ...(state.listsById[action.meta.listId]?.statuses || [])
                            .map(status => status.id === action.meta.status.id
                                ? { ...status, ...action.meta.model, }
                                : status),
                    ],
                },
            },
        };

    case QualityHubActionTypes.UpdateTaskStatusRejected:
        if (!state.listsById[action.meta.listId]) {
            return state;
        }

        if (action.payload.isOffline) {
            return {
                ...state,
                offlineQueue: {
                    ...state.offlineQueue,
                    operations: state.offlineQueue.operations.concat({
                        id: uniqueId("QH-OFFLINE-OP_"),
                        type: "UPDATE_STATUS",
                        data: action.meta,
                    }),
                },
                signatureQueue: !action.meta.model.signatureQueueItemId
                    ? state.signatureQueue
                    : {
                        ...state.signatureQueue,
                        operations: state.signatureQueue.operations.filter(
                            (op) => op.id !== action.meta.model.signatureQueueItemId,
                        ),
                    },
            };
        }

        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.meta.listId]: {
                    ...state.listsById[action.meta.listId],
                    statuses: [
                        // Revert the status if the update failed
                        ...(state.listsById[action.meta.listId]?.statuses || [])
                            .map(status => status.id === action.meta.status.id
                                ? action.meta.status
                                : status),
                    ],
                },
            },
            signatureQueue: !action.meta.model.signatureQueueItemId
                ? state.signatureQueue
                : {
                    ...state.signatureQueue,
                    lastSubmitError: "PIN validation failed on server. Please try again.",
                },
        };

    case QualityHubActionTypes.UpdateTaskStatusFulfilled:
        if (!state.listsById[action.meta.listId]) {
            return state;
        }

        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.meta.listId]: {
                    ...state.listsById[action.meta.listId],
                    statuses: [
                        ...(state.listsById[action.meta.listId]?.statuses || [])
                            .map((status) => status.id === action.payload.id
                                ? {
                                    ...action.payload,
                                    reportedOn: new Date(action.payload.reportedOn),
                                }
                                : status),
                    ],
                },
            },
            signatureQueue: !action.meta.model.signatureQueueItemId
                ? state.signatureQueue
                : {
                    ...state.signatureQueue,
                    operations: state.signatureQueue.operations.filter(
                        (op) => op.id !== action.meta.model.signatureQueueItemId,
                    ),
                },
        };

    case QualityHubActionTypes.DeleteTaskStatusPending:
        if (!state.listsById[action.meta.listId]) {
            return state;
        }

        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.meta.listId]: {
                    ...state.listsById[action.meta.listId],
                    statuses: [
                        // Remove the status in advance
                        ...(state.listsById[action.meta.listId]?.statuses || [])
                            .filter(status => status.id !== action.meta.status.id),
                    ],
                },
            },
        };

    case QualityHubActionTypes.DeleteTaskStatusRejected:
        if (!state.listsById[action.meta.listId]) {
            return state;
        }

        if (action.payload.isOffline) {
            return {
                ...state,
                offlineQueue: {
                    ...state.offlineQueue,
                    operations: state.offlineQueue.operations.concat({
                        id: uniqueId("QH-OFFLINE-OP_"),
                        type: "DELETE_STATUS",
                        data: action.meta,
                    }),
                },
            };
        }

        return {
            ...state,
            listsById: {
                ...state.listsById,
                [action.meta.listId]: {
                    ...state.listsById[action.meta.listId],
                    statuses: [
                        // Re-add the status if the deletion failed
                        ...(state.listsById[action.meta.listId]?.statuses || [])
                            .concat(action.meta.status),
                    ],
                },
            },
        };

    case QualityHubActionTypes.QueryResourceTagsPending:
        return {
            ...state,
            loadingResourceTags: true,
        };

    case QualityHubActionTypes.QueryResourceTagsRejected:
        return {
            ...state,
            loadingResourceTags: false,
        };

    case QualityHubActionTypes.QueryResourceTagsFulfilled:
        return {
            ...state,
            resourceTagsByIds: action.payload.items.reduce((acc, item) => {
                acc[item.id] = item;
                return acc;
            }, state.resourceTagsByIds),
            allTagIds: state.allTagIds.concat(
                action.payload.items
                    .filter((item) => state.allTagIds.every((id) => id !== item.id))
                    .map((item) => item.id),
            ),
            tagsQueryWindow: action.payload.items.map((item) => item.id),
            loadingResourceTags: false,
        };

    case QualityHubActionTypes.QueryPinHashesPending:
        return {
            ...state,
            loadingPinHashes: true,
        };

    case QualityHubActionTypes.QueryPinHashesRejected:
        return {
            ...state,
            loadingPinHashes: false,
            hasLoadedPinHashes: true,
        };

    case QualityHubActionTypes.QueryPinHashesFulfilled:
        return {
            ...state,
            pinHashes: action.payload.map((entry) => entry.pinCodeHash),
            loadingPinHashes: false,
            hasLoadedPinHashes: true,
        };

    case QualityHubActionTypes.QueueSignatureStatusChange: {
        const withoutItem = state.signatureQueue.operations.filter((op) => {
            return !(
                op.listId === action.operation.listId &&
                op.model.itemId === action.operation.model.itemId
            );
        });

        return {
            ...state,
            signatureQueue: {
                ...state.signatureQueue,
                operations: withoutItem.concat(action.operation),
                lastSubmitError: undefined,
            },
        };
    }

    case QualityHubActionTypes.RemoveSignatureStatusChange:
        return {
            ...state,
            signatureQueue: {
                ...state.signatureQueue,
                operations: state.signatureQueue.operations.filter(
                    (op) => op.id !== action.operationId,
                ),
            },
        };

    case QualityHubActionTypes.SubmitSignatureQueuePending:
        return {
            ...state,
            signatureQueue: {
                ...state.signatureQueue,
                isSubmitting: true,
                lastSubmitError: undefined,
            },
        };

    case QualityHubActionTypes.SubmitSignatureQueueFulfilled:
        return {
            ...state,
            signatureQueue: {
                ...state.signatureQueue,
                isSubmitting: false,
            },
        };

    case QualityHubActionTypes.SubmitSignatureQueueRejected:
        return {
            ...state,
            signatureQueue: {
                ...state.signatureQueue,
                isSubmitting: false,
                lastSubmitError: action.errorMessage,
            },
        };

    case QualityHubActionTypes.RemoveFromOfflineQueue:
        return {
            ...state,
            offlineQueue: {
                ...state.offlineQueue,
                operations: state.offlineQueue.operations.filter(
                    (op) => op.id !== action.operationId,
                ),
            },
        };

    case QualityHubActionTypes.QueryOperationalTargetsPending:
        return {
            ...state,
            operationalTargets: {
                ...state.operationalTargets,
                isLoading: true,
            },
        };

    case QualityHubActionTypes.QueryOperationalTargetsRejected:
        return {
            ...state,
            operationalTargets: {
                ...state.operationalTargets,
                isLoading: false,
                hasLoaded: true,
            },
        };

    case QualityHubActionTypes.QueryOperationalTargetsFulfilled:
        return {
            ...state,
            operationalTargets: {
                items: action.payload.items,
                isLoading: false,
                hasLoaded: true,
            },
        };

    case QualityHubActionTypes.CreateOperationalTargetFulfilled:
    case QualityHubActionTypes.UpdateOperationalTargetFulfilled:
        return {
            ...state,
            operationalTargets: {
                ...state.operationalTargets,
                items: state.operationalTargets.items
                    .filter(item => item.id !== action.payload.id)
                    .concat([action.payload]),
            },
        };

    case QualityHubActionTypes.DeleteOperationalTargetFulfilled:
        return {
            ...state,
            operationalTargets: {
                ...state.operationalTargets,
                items: state.operationalTargets.items
                    .filter(item => item.id !== action.meta.target.id),
            },
        };

    case QualityHubActionTypes.OperationalTargetCreated:
    case QualityHubActionTypes.OperationalTargetUpdated:
        return {
            ...state,
            operationalTargets: {
                ...state.operationalTargets,
                items: state.operationalTargets.items
                    .filter(item => item.id !== action.target.id)
                    .concat([action.target]),
            },
        };

    case QualityHubActionTypes.OperationalTargetDeleted:
        return {
            ...state,
            operationalTargets: {
                ...state.operationalTargets,
                items: state.operationalTargets.items
                    .filter(item => item.id !== action.targetId),
            },
        };

    default:
        return state;
    }
}

